import Vue from "vue";
import Router from "vue-router";
import store from "../store";
import { hasAnyPermission, hasAnyRole, hasPermission, normalizeRoleNames } from "../utils/access";

Vue.use(Router);

const router = new Router({
  mode: "history",
  routes: [
    {
      path: "/login",
      name: "login",
      component: () => import("../views/Login.vue"),
      meta: { public: true },
    },
    {
      path: "/register",
      name: "register",
      component: () => import("../views/Register.vue"),
      meta: { public: true },
    },
    {
      path: "/forgot-password",
      name: "forgot-password",
      component: () => import("../views/ForgotPassword.vue"),
      meta: { public: true },
    },
    {
      path: "/reset-password",
      name: "reset-password",
      component: () => import("../views/ResetPassword.vue"),
      meta: { public: true },
    },
    {
      path: "/change-password",
      name: "change-password",
      component: () => import("../views/ChangePassword.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/",
      component: () => import("../layouts/MainLayout.vue"),
      meta: { requiresAuth: true },
      children: [
        {
          path: "",
          name: "home",
          component: () => import("../views/Home.vue"),
        },
        {
          path: "security/users",
          name: "security-users",
          component: () => import("../views/SecurityUsers.vue"),
          meta: { requiresAdmin: true },
        },
        {
          path: "security/roles",
          name: "security-roles",
          component: () => import("../views/SecurityRoles.vue"),
          meta: { requiresAdmin: true },
        },
        {
          path: "security/audit",
          name: "security-audit",
          component: () => import("../views/SecurityAudit.vue"),
          meta: { requiresAdmin: true },
        },

        {
          path: "config/document-design",
          name: "document-design",
          component: () => import("../views/DocumentDesignConfig.vue"),
          meta: { requiresAdmin: true },
        },
        {
          path: "config/remito-templates",
          name: "config-remito-templates",
          component: () => import("../views/Empresas/ConfiguracionRemitos.vue"),
          meta: { requiresAdmin: true },
        },
        {
          path: "config/email",
          name: "email-config",
          component: () => import("../views/EmailConfigView.vue"),
          meta: { requiresAdmin: true },
        },
        {
          path: "config/iibb",
          name: "config-iibb",
          component: () => import("../views/IibbTaxesConfig.vue"),
          meta: { requiresAdmin: true },
        },
        {
          path: "config/points-of-sale",
          name: "config-points-of-sale",
          component: () => import("../views/PointOfSaleView.vue"),
          meta: { requiresAdmin: true },
        },
        {
          path: "config/afip",
          name: "config-afip",
          component: () => import("../views/AfipConfigView.vue"),
          meta: { requiresAdmin: true },
        },
        {
          path: "config/webhooks",
          name: "config-webhooks",
          component: () => import("../views/WebhooksView.vue"),
          meta: { requiresAdmin: true },
        },
        {
          path: "sales/catalog",
          name: "sales-catalog",
          component: () => import("../views/SalesPriceList.vue"),
          props: { viewMode: "sales" },
        },
        {
          path: "sales/current-accounts",
          name: "sales-current-accounts",
          component: () => import("../views/SalesCurrentAccounts.vue"),
          props: { viewMode: "customer" },
          meta: { requiredPermission: "sales.current_accounts.view" },
        },
        {
          path: "sales/collections",
          name: "sales-collections",
          component: () => import("../views/SalesCurrentAccounts.vue"),
          props: { viewMode: "customer", collectionsOnly: true },
          meta: { requiredPermission: "sales.current_accounts.view" },
        },
        {
          path: "sales/budgets",
          name: "sales-budgets",
          component: () => import("../views/SalesBudgets.vue"),
          meta: { requiredPermission: "sales.budget.read" },
        },
        {
          path: "sales/orders",
          name: "sales-orders",
          component: () => import("../views/SalesBudgets.vue"),
          props: { ordersOnly: true },
          meta: { requiredPermission: "sales.budget.read" },
        },
        {
          path: "sales/counter-invoices",
          name: "sales-counter-invoices",
          component: () => import("../views/SalesCounterInvoices.vue"),
          props: { billingChannel: "COUNTER" },
          meta: { requiredPermission: "sales.invoice.read" },
        },
        {
          path: "sales/current-account-invoices",
          name: "sales-current-account-invoices",
          component: () => import("../views/SalesCounterInvoices.vue"),
          props: { billingChannel: "CURRENT_ACCOUNT" },
          meta: { requiredPermission: "sales.current_account_invoice.read" },
        },
        {
          path: "sales/order-management",
          name: "sales-order-management",
          component: () => import("../views/SalesOrderManagement.vue"),
          meta: { requiredPermission: "sales.order.review" },
        },
        {
          path: "sales/orders/new",
          name: "sales-direct-order",
          component: () => import("../views/SalesBudgets.vue"),
          props: { directOrder: true },
          meta: { requiredPermission: "sales.budget.create" },
        },
        {
          path: "sales/approvals",
          name: "sales-approvals",
          component: () => import("../views/SalesApprovals.vue"),
          meta: { requiredPermission: "sales.order.review" },
        },
        {
          path: "sales/commissions",
          name: "sales-commissions",
          component: () => import("../views/SalesCommissions.vue"),
          meta: { requiredPermission: "sales.commission.manage" },
        },
        {
          path: "sales/commercial-config",
          name: "sales-commercial-config",
          component: () => import("../views/CommercialConfig.vue"),
          meta: { requiredPermission: "sales.commercial_config.manage" },
        },
        {
          path: "sales/reports",
          name: "sales-reports",
          component: () => import("../views/CommercialReports.vue"),
          meta: { requiresAdmin: true },
        },

        {
          path: "purchases/management",
          name: "purchases-management",
          component: () => import("../views/PurchasesManagement.vue"),
          meta: { requiredPermission: "purchases.read" },
        },
        {
          path: "purchases/suppliers",
          name: "purchases-suppliers",
          component: () => import("../views/SalesCurrentAccounts.vue"),
          props: { viewMode: "supplier" },
          meta: { requiredPermission: "purchases.read" },
        },
        {
          path: "pedidos-internos",
          name: "internal-orders-list",
          component: () => import("../views/InternalOrdersList.vue"),
          meta: { requiredPermission: "internal_orders.read" },
        },
        {
          path: "pedidos-internos/pendientes",
          name: "internal-orders-dashboard",
          component: () => import("../views/InternalOrdersDashboard.vue"),
          meta: { requiredPermission: "internal_orders.manage" },
        },
        {
          path: "pedidos-internos/nuevo",
          name: "internal-order-create",
          component: () => import("../views/InternalOrderCreate.vue"),
          meta: { requiredPermission: "internal_orders.create" },
        },
        {
          path: "pedidos-internos/:id/editar",
          name: "internal-order-edit",
          component: () => import("../views/InternalOrderCreate.vue"),
          meta: { requiredPermission: "internal_orders.create" },
        },
        {
          path: "pedidos-internos/:id",
          name: "internal-order-detail",
          component: () => import("../views/InternalOrderDetail.vue"),
          meta: { requiredPermission: "internal_orders.read" },
        },
        {
          path: "accounting/periods",
          name: "accounting-periods",
          component: () => import("../views/AccountingPeriodsView.vue"),
          meta: { requiresAccounting: true },
        },
        {
          path: "accounting/reconciliation",
          name: "accounting-reconciliation",
          component: () => import("../views/AccountingReconciliationView.vue"),
          meta: { requiresAccounting: true },
        },
        {
          path: "accounting/reports",
          name: "accounting-reports",
          component: () => import("../views/AccountingReportsView.vue"),
          meta: { requiresAccounting: true },
        },
        {
          path: "treasury",
          name: "treasury",
          component: () => import("../views/TreasuryView.vue"),
          meta: { requiresAccounting: true },
        },
        {
          path: "ops/monitoring",
          name: "ops-monitoring",
          component: () => import("../views/OpsMonitoringView.vue"),
          meta: { requiresAccounting: true },
        },
        {
          path: "sales/price-list",
          redirect: { name: "sales-catalog" },
        },
        {
          path: "pricing/management",
          name: "pricing-management",
          component: () => import("../views/SalesPriceList.vue"),
          props: { viewMode: "pricing" },
          meta: { requiresAdmin: true },
        },
        {
          path: "pricing/dashboard",
          name: "pricing-dashboard",
          component: () => import("../views/PricingAdminDashboard.vue"),
          meta: { requiresAdmin: true },
        },
        {
          path: "pricing/bonuses",
          name: "pricing-bonuses",
          component: () => import("../views/PricingBonuses.vue"),
          meta: { requiresAdmin: true },
        },
        {
          path: "stock/catalog",
          name: "stock-catalog",
          component: () => import("../views/StockCatalog.vue"),
        },
        {
          path: "stock/inventory",
          name: "stock-inventory",
          component: () => import("../views/StockInventory.vue"),
        },
        {
          path: "stock/price-list",
          name: "stock-price-list",
          component: () => import("../views/StockPriceList.vue"),
        },

      ],
    },
    { path: "*", redirect: "/" },
  ],
});

function isCounterOperatorRole(roles) {
  const normalized = normalizeRoleNames(roles);
  return normalized.includes("operario_mostrador")
    || normalized.includes("operario mostrador")
    || normalized.includes("mostrador");
}

router.beforeEach((to, from, next) => {
  const isPublic = to.matched.some((record) => record.meta.public);
  const isAuthed = store.getters["auth/isAuthenticated"];

  if (!isPublic && !isAuthed) {
    next({ name: "login", query: { redirect: to.fullPath } });
    return;
  }

  if (isPublic && isAuthed && to.name === "login") {
    next({ name: "home" });
    return;
  }

  const requiresAdmin = to.matched.some((record) => record.meta.requiresAdmin);
  if (requiresAdmin) {
    const user = store.getters["auth/user"] || {};
    if (!hasAnyRole(user, ["admin"])) {
      next({ name: "home" });
      return;
    }
  }

  const user = store.getters["auth/user"] || {};
  const roles = Array.isArray(user.roles) ? user.roles : [];
  const normalizedRoles = normalizeRoleNames(roles);

  const requiresAccounting = to.matched.some((record) => record.meta.requiresAccounting);
  if (requiresAccounting) {
    const canAccessAccounting = hasAnyRole(user, ["admin", "security", "seguridad", "contabilidad", "accounting"])
      || hasAnyPermission(user, ["accounting.period.view", "accounting.reconcile.view", "accounting.report.view"]);
    if (!canAccessAccounting) {
      next({ name: "home" });
      return;
    }
  }

  const requiredPermission = to.matched
    .map((record) => record.meta.requiredPermission)
    .find(Boolean);
  if (requiredPermission && !hasPermission(user, requiredPermission)) {
    next({ name: "home" });
    return;
  }

  const isCounterOperator = isCounterOperatorRole(roles);
  if (isCounterOperator) {
    const isAllowedRoute = to.name === "sales-catalog" || to.name === "sales-budgets" || to.name === "sales-orders";
    if (!isAllowedRoute) {
      next({ name: "sales-catalog" });
      return;
    }
    if (to.query && to.query.tab) {
      next({ name: "sales-catalog" });
      return;
    }
  }

  next();
});

export default router;
